import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';

const api = apiService();

// ————————————————————————————————
// Async actions for Payments
// ————————————————————————————————

/**
 * Create a Stripe Setup Intent (for saving a card).
 * POST /payment/create-setup-intent → { clientSecret }
 */
export const createSetupIntent = createAsyncThunk(
  'payments/createSetupIntent',
  async () => {
    const response = await api.post('/payment/create-setup-intent', {});
    return response.data; // { clientSecret: string }
  }
);

/**
 * Fetch saved payment cards for current user.
 * GET /payment/cards → Array<Card>
 */
export const fetchCards = createAsyncThunk(
  'payments/fetchCards',
  async () => {
    const response = await api.get('/payment/cards');
    return response?.data; // Array of card objects
  }
);

/**
 * Get detail of a specific PaymentIntent.
 * GET /payment/:paymentId → PaymentDetail
 */
export const fetchPaymentDetail = createAsyncThunk(
  'payments/fetchPaymentDetail',
  async (paymentId: string) => {
    const response = await api.get(`/payment/${paymentId}`);
    return response.data; // PaymentIntent detail
  }
);

/**
 * Charge using a saved card.
 * POST /payment/pay-with-saved
 *   { amount: string, paymentMethodId: string }
 */
export const payWithSavedCard = createAsyncThunk(
  'payments/payWithSavedCard',
  async (params: { amount: number; paymentMethodId: string }) => {
    const response = await api.post('/payment/pay-with-saved', params);
    return response.data; // Charge result
  }
);

/**
 * Charge without saving card (one-off).
 * POST /payment/pay-no-save
 *   { amount: string }
 */
export const payWithoutSavedCard = createAsyncThunk(
  'payments/payWithoutSavedCard',
  async (params: { amount: number }) => {
    const response = await api.post('/payment/pay-no-save', params);
    return response.data; // Charge result
  }
);

export const removeCard = createAsyncThunk(
  'payments/card',
  async (id: string) => {
    const response = await api.delete(`/payment/card/${id}`);
    return response.data; // Charge result
  }
);

export const getMyTransaction = createAsyncThunk(
  'payments/transaction',
  async (params: { page?: number, limit?: number }) => {
    const response = await api.get('/wallet', {params});
    return response.data; // Charge result
  }
);

// ————————————————————————————————
// State and Slice
// ————————————————————————————————

interface PaymentState {
  clientSecret: string | null;
  cards: any[];
  paymentDetail: any | null;
  lastCharge: any | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: PaymentState = {
  clientSecret: null,
  cards: [],
  paymentDetail: null,
  lastCharge: null,
  status: 'idle',
  error: null,
};

const paymentSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // createSetupIntent
    builder
      .addCase(createSetupIntent.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createSetupIntent.fulfilled, (state, action) => {
        state.clientSecret = action.payload.clientSecret;
        state.status = 'succeeded';
      })
      .addCase(createSetupIntent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
        toast.error('Failed to create setup intent');
      });

    // fetchCards
    builder
      .addCase(fetchCards.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCards.fulfilled, (state, action) => {
        state.cards = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchCards.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
        toast.error('Failed to load cards');
      });

    // fetchPaymentDetail
    builder
      .addCase(fetchPaymentDetail.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPaymentDetail.fulfilled, (state, action) => {
        state.paymentDetail = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchPaymentDetail.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
        toast.error('Failed to fetch payment detail');
      });

    // payWithSavedCard
    builder
      .addCase(payWithSavedCard.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(payWithSavedCard.fulfilled, (state, action) => {
        state.lastCharge = action.payload;
        state.status = 'succeeded';
        toast.success('Payment successful');
      })
      .addCase(payWithSavedCard.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
        toast.error('Payment with saved card failed');
      });

    // payWithoutSavedCard
    builder
      .addCase(payWithoutSavedCard.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(payWithoutSavedCard.fulfilled, (state, action) => {
        state.lastCharge = action.payload;
        state.status = 'succeeded';
        toast.success('Payment successful');
      })
      .addCase(payWithoutSavedCard.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
        toast.error('One-off payment failed');
      });

    // removeCard
    builder
      .addCase(removeCard.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(removeCard.fulfilled, (state, action) => {
        state.lastCharge = action.payload;
        state.status = 'succeeded';
        toast.success('Payment successful');
      })
      .addCase(removeCard.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
        toast.error('One-off payment failed');
      });
  },
});

export default paymentSlice.reducer;
